import React from 'react';

import { Box } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { SortBy } from 'react-instantsearch-dom';
import { useParams } from 'react-router-dom';

import { useSERPNavigation } from '../../../../utils/hooks';
import { HITS_TYPES } from '../../const';
import { CategoryNavigationBlock } from '../CategoryNavigationBlock';
import { ExpertNavigationBlock } from '../ExpertNavigationBlock';
import { FilterWrapper } from '../FilterWrapper';
import { HierarchicalMenu as CustomHierarchicalMenu } from '../HierarchicalMenu';
import { IndexMenu } from '../IndexMenu';
import { RefinementList as CustomRefinementList } from '../RefinementList';
import useStyles from './styles';

interface FiltersPanelProps {
  isMobile: boolean;
  resetTypeFilter?: boolean;
  isSearching?: boolean;
  tenantName?: string | null;
}

const FiltersPanel: React.FC<FiltersPanelProps> = ({
  isMobile,
  tenantName,
  resetTypeFilter = false,
  isSearching = false,
}) => {
  const { t } = useTranslation();
  const classes = useStyles();
  const params = useParams();
  const serpType = useSERPNavigation();

  const onCategoriesPage = !!params?.category;
  const onExpertPage = !!params?.expertSlug;
  const onInsightsPage = serpType === HITS_TYPES.NEWS;
  const onGlossaryPage = serpType === HITS_TYPES.GLOSSARY;

  const sortedIndexName = `${process.env.REACT_APP_ALGOLIA_INDEX_NAME}_${tenantName}_title_asc`;

  return (
    <>
      <Box className={classes.filtersPanel}>
        {onGlossaryPage && (
          <>
            <div className={classes.sortWrapper}>
              <SortBy
                defaultRefinement={
                  isSearching ? process.env.REACT_APP_ALGOLIA_INDEX_NAME : sortedIndexName
                }
                items={[
                  { value: sortedIndexName, label: 'Sort' },
                  { value: process.env.REACT_APP_ALGOLIA_INDEX_NAME, label: 'Default' },
                ]}
              />
            </div>
            <FilterWrapper title={t('page.discover.index')} withoutToggle>
              <IndexMenu attribute="letter_index" limit={50} />
            </FilterWrapper>
          </>
        )}
        {onExpertPage && (
          <FilterWrapper title={t('page.discover.experts')} withoutToggle>
            <ExpertNavigationBlock attribute="user" />
          </FilterWrapper>
        )}
        {!onInsightsPage && (
          <FilterWrapper title={t('page.discover.categories')} withoutToggle={onCategoriesPage}>
            {onCategoriesPage ? (
              <>
                <CategoryNavigationBlock attributes={['categories.lvl0', 'categories.lvl1']} />
              </>
            ) : (
              <CustomHierarchicalMenu attributes={['categories.lvl0', 'categories.lvl1']} />
            )}
          </FilterWrapper>
        )}
        {!(onInsightsPage || onGlossaryPage) && (
          <FilterWrapper title={t('page.discover.level')}>
            <CustomRefinementList attribute="difficulty.title" />
          </FilterWrapper>
        )}
        {!(onExpertPage || onGlossaryPage) && (
          <FilterWrapper title={t('page.discover.experts')}>
            <CustomRefinementList
              attribute="user"
              showMore
              searchable
              limit={5}
              showMoreLimit={50}
              delay={isMobile ? 500 : 0}
            />
          </FilterWrapper>
        )}
        {!(onInsightsPage || onGlossaryPage) && (
          <FilterWrapper title={t('page.discover.duration')}>
            <CustomRefinementList attribute="duration_interval" />
          </FilterWrapper>
        )}
        {resetTypeFilter && (
          <FilterWrapper title={t('page.discover.type')}>
            <CustomRefinementList attribute="searchable_type" />
          </FilterWrapper>
        )}
      </Box>
    </>
  );
};

export default FiltersPanel;
